import React, { useEffect, useState } from 'react'
import {
  Box,
  Card,
  CardContent,
  Typography,
  Switch,
  FormControlLabel,
  TextField,
  Alert,
  Checkbox,
  Button,
  Stack,
  Divider,
  LinearProgress,
} from '@mui/material'
import axios from 'axios'
import API from '../../../../services/API'

export default function DBSSettingsPage() {
  const [settings, setSettings] = useState({
    enabled: false,
    organisationName: '',
    declarationAccepted: false,
    myName: false,
  })
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [errorSaving, setErrorSaving] = useState(false)

  const fetchSettings = async () => {
    setLoading(true)
    await API.get('/settings/dbs-company-settings')
      .then((res) => {
        if (res.status === 200) {
          setSettings((prev) => ({
            ...prev,
            organisationName: res.data.orgName,
            ...res.data?.staffGovernanceSettings?.dbsUpdateServiceSettings,
          }))
        }
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    fetchSettings()
  }, [])

  const handleChange = (field, value) => {
    setSettings((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  const handleSave = async () => {
    setSaving(true)
    await API.post('/settings/dbs-company-settings', { ...settings })
      .then((res) => {
        if (res.status === 200) {
          fetchSettings()
        }
      })
      .catch((e) => setErrorSaving(true))
      .finally(() => setSaving(false))
  }

  const canSave =
    !settings.enabled ||
    (settings.organisationName &&
      settings.declarationAccepted &&
      settings.myName)

  return (
    <Stack spacing={3}>
      <Stack gap={1}>
        <Typography variant="title2" gutterBottom>
          DBS Update Service Settings
        </Typography>
        {loading && <LinearProgress />}
      </Stack>
      <Box>
        <FormControlLabel
          control={
            <Switch
              checked={settings.enabled ?? false}
              onChange={(e) => handleChange('enabled', e.target.checked)}
            />
          }
          label="Enable automated DBS Update Service monitoring"
        />

        <Typography variant="body2" color="text.secondary">
          AmbuNet will run nightly checks for eligible completed DBS records.
        </Typography>
      </Box>

      <Divider />

      {settings?.enabled && (
        <Stack spacing={3}>
          <TextField
            label="Organisation name"
            value={settings.organisationName ?? settings.orgName}
            onChange={(e) => handleChange('organisationName', e.target.value)}
            fullWidth
            required={settings.enabled}
          />

          <Alert severity="info">
            Before enabling DBS Update Service monitoring, your organisation
            must confirm that it has the authority to carry out these checks.
          </Alert>

          <FormControlLabel
            control={
              <Checkbox
                checked={settings.declarationAccepted ?? false}
                onChange={(e) =>
                  handleChange('declarationAccepted', e.target.checked)
                }
              />
            }
            label="I confirm I have the authority of the individual to which this DBS Certificate number relates to receive up-to-date information (within the meaning of section 116A of the Police Act 1997) in relation to their criminal record DBS Certificate for the purposes of asking an exempted question within the meaning of section 113A of the Police Act 1997; or in relation to their enhanced criminal record DBS Certificate for the purposes of asking an exempted question for a prescribed purpose within the meaning of section 113B of the Police Act 1997."
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={settings.myName ?? false}
                onChange={(e) => handleChange('myName', e.target.checked)}
              />
            }
            label="I understand that my name will appear on Update Service checks and will be made available to the DBS Update Service."
          />

          {settings.declarationAcceptedAt && (
            <Alert severity="success">
              Declaration accepted on{' '}
              {new Date(settings.declarationAcceptedAt).toLocaleString()} by{' '}
              {settings.declarationAcceptedBy.firstName}{' '}
              {settings.declarationAcceptedBy.lastName}
            </Alert>
          )}

          <Alert severity="warning">
            AmbuNet will only check completed DBS records where the employee is
            active and the DBS record has not been removed from Update Service
            monitoring.
          </Alert>
        </Stack>
      )}
      <Box>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!canSave || saving}
        >
          {saving ? 'Saving...' : 'Save DBS Settings'}
        </Button>
      </Box>
    </Stack>
  )
}
